import {
  Box,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  UnorderedList,
  ListItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
} from '@chakra-ui/react';
import React from 'react';

import { error as commonError } from '@finley-technologies/common';

interface StructuredErrorsProps {
  errors: commonError.StructuredError[];
}

export const StructuredErrors: React.FC<StructuredErrorsProps> = ({
  errors,
}) => {
  if (!errors || errors.length === 0) {
    return null;
  }

  return (
    <Box>
      {errors.map((err, idx) => (
        <Box
          // eslint-disable-next-line react/no-array-index-key
          key={idx}
          border="1px"
          borderColor="gray.200"
          p={4}
          mb={4}
          borderRadius="md"
        >
          <Heading as="h3" size="md" mb={2}>
            {err.externalMessage}
          </Heading>

          {err.properties && (
            <Box mb={4}>
              <Heading as="h4" size="sm" mb={2}>
                Details
              </Heading>
              <UnorderedList>
                {Object.entries(err.properties).map(([key, value]) => (
                  <ListItem key={key}>
                    <Text as="span" fontWeight="bold">
                      {key}:
                    </Text>{' '}
                    {value}
                  </ListItem>
                ))}
              </UnorderedList>
            </Box>
          )}

          {err.internalProperties && (
            <Box mb={4}>
              <Heading as="h4" size="sm" mb={2}>
                Internal Properties
              </Heading>
              <UnorderedList>
                {Object.entries(err.internalProperties).map(([key, value]) => (
                  <ListItem key={key}>
                    <Text as="span" fontWeight="bold">
                      {key}:
                    </Text>{' '}
                    {value}
                  </ListItem>
                ))}
              </UnorderedList>
            </Box>
          )}

          {err.tableData &&
            err.tableData.rows &&
            err.tableData.rows.length > 0 && (
              <Box>
                <Heading as="h4" size="sm" mb={2}>
                  Table Data
                </Heading>
                <Table size="sm" variant="striped" colorScheme="gray">
                  <Thead>
                    <Tr>
                      {Object.keys(err.tableData.rows[0]).map((col) => (
                        <Th key={col}>{col}</Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {err.tableData.rows.map((row, rIdx) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <Tr key={rIdx}>
                        {Object.values(row).map((val, cIdx) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <Td key={cIdx}>{val || ''}</Td>
                        ))}
                      </Tr>
                    ))}
                    {err.tableData.omittedRows > 0 && (
                      <Tr>
                        <Td colSpan={Object.keys(err.tableData.rows[0]).length}>
                          ...and {err.tableData.omittedRows} more rows omitted.
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>
            )}
        </Box>
      ))}
    </Box>
  );
};

interface StructuredErrorsModalProps {
  isOpen: boolean;
  onClose: () => void;
  errors: commonError.StructuredError[];
}

export const StructuredErrorsModal: React.FC<StructuredErrorsModalProps> = ({
  isOpen,
  onClose,
  errors,
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="inside" size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Errors</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <StructuredErrors errors={errors} />
        </ModalBody>
        <ModalFooter>
          <Button variant="solid" colorScheme="blue" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
